import { memo, useCallback, useMemo, useState } from 'react';
import { Focusable, Key } from 'react-aria-components';

import {
  Button,
  Flex,
  Label,
  PillSelectValue,
  Radio,
  RadioGroup,
  SelectPills,
  SelectPillsOption,
  Separator,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { useDebounceCallback, useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { getCountryByCode, Id, isArray } from '@/utils';

const SUPPORTED_COUNTRY_CODES = [
  'AD',
  'AE',
  'AL',
  'AT',
  'AZ',
  'BA',
  'BE',
  'BG',
  'BH',
  'BR',
  'BY',
  'CH',
  'CR',
  'CY',
  'CZ',
  'DE',
  'DK',
  'DO',
  'EE',
  'EG',
  'ES',
  'FI',
  'FO',
  'FR',
  'GB',
  'GE',
  'GI',
  'GL',
  'GR',
  'GT',
  'HR',
  'HU',
  'IE',
  'IL',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JO',
  'KW',
  'KZ',
  'LB',
  'LC',
  'LI',
  'LT',
  'LU',
  'LV',
  'MC',
  'MD',
  'ME',
  'MK',
  'MR',
  'MT',
  'MU',
  'MZ',
  'NL',
  'NO',
  'PK',
  'PL',
  'PS',
  'PT',
  'QA',
  'RO',
  'RS',
  'SA',
  'SC',
  'SE',
  'SI',
  'SK',
  'SM',
  'SV',
  'TL',
  'TN',
  'TR',
  'UA',
  'VA',
  'VG',
  'XK',
];

export type IbanValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const IIbanValidator = ({ fieldId, validatorId }: IbanValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  const [isSpecific, setIsSpecific] = useState(() => {
    if (isArray(validator?.config.countryCodes) && validator.config.countryCodes.length > 0) {
      return true;
    }
    return false;
  });

  const onSetCountryCodes = useDebounceCallback(
    useCallback(
      (countryCodes: string[] | undefined) => mutate((f) => (f.config.countryCodes = countryCodes)),
      [mutate]
    ),
    500
  );

  const buttonComponent = (
    <Button>
      <PillSelectValue>
        {(item?: Id | null) => {
          return item ? (
            <TooltipTrigger>
              <Tooltip variant="primary">
                <Text>{getCountryByCode(item.id)?.name}</Text>
              </Tooltip>

              <Focusable>
                <Text>{getCountryByCode(item.id)?.emoji}</Text>
              </Focusable>
            </TooltipTrigger>
          ) : null;
        }}
      </PillSelectValue>
      <ChevronIcon rotation="down" />
    </Button>
  );

  const onChangeSpecific = useCallback(
    (key: Key) => {
      setIsSpecific(key.toString() === 'specific');
      onSetCountryCodes(undefined);
    },
    [onSetCountryCodes]
  );

  const countryCodes = useMemo(() => {
    return SUPPORTED_COUNTRY_CODES.toSorted((a, b) => {
      const nameA = getCountryByCode(a)?.name ?? '';
      const nameB = getCountryByCode(b)?.name ?? '';
      return nameA.localeCompare(nameB);
    }).map((code) => ({ id: code }));
  }, []);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />

      <Flex col className="gap-sm">
        <Label>Countries</Label>

        <RadioGroup onChange={onChangeSpecific} defaultValue={isSpecific ? 'specific' : 'all'}>
          <Radio value="all">
            <Text slot="description">All supported countries</Text>
          </Radio>
          <Radio value="specific">
            <Text slot="description">Select specific countries</Text>
          </Radio>
        </RadioGroup>

        {isSpecific ? (
          <SelectPills<Id>
            value={validator?.config.countryCodes as string[] | undefined}
            onChange={(countryCodes) => onSetCountryCodes(countryCodes.map((c) => c.toString()))}
            optionRenderFn={(option) => (
              <SelectPillsOption value={option}>
                {option ? (
                  <div className="gap-sm flex items-center">
                    <Text>{getCountryByCode(option.id)?.emoji}</Text>
                    <Text>{getCountryByCode(option.id)?.name}</Text>
                  </div>
                ) : null}
              </SelectPillsOption>
            )}
            items={countryCodes}
            buttonComponent={buttonComponent}
          />
        ) : null}
      </Flex>

      <ValidationFormSettings fieldId={fieldId} validatorId={validatorId} />
    </div>
  );
};

export const IbanValidator = memo(IIbanValidator) as typeof IIbanValidator;
